import { mdiHistory, mdiPaperclip } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FunctionComponent, ReactNode } from "react";
import { currencyFormat } from "../../../../utils/currency_format";
import { formatAmountWithCommas } from "../../../../ui/pages/content_panel/utils/helpers";
import { urls } from "../../../../utils/urls";
import { PaymentRequestModel } from "../../model/payment_request_model";
import {
  StatusBadge,
  MobileRecordCard,
  MobileRecordCardList,
} from "../../../../ui/new_components/common/responsive";
import Pagination from "../../../../ui/new_components/common/pagination";

type Align = "left" | "right";

interface Column {
  label: string;
  align?: Align;
  render: (d: PaymentRequestModel) => ReactNode;
}

interface PaymentRequestTableProps {
  rows: PaymentRequestModel[];
  page: number;
  total: number;
  count: number;
  onPageChange: (p: number) => void;
  onShowHistory: (d: PaymentRequestModel) => void;
  onShowAttachment: (d: PaymentRequestModel) => void;
  variant: "list" | "approval";
}

const fmtDate = (v: any) => {
  if (v === null || v === undefined || v === "") return "--";
  if ((typeof v === "string" && v.includes("-")) || v instanceof Date) {
    return moment(v, moment.ISO_8601, true).isValid()
      ? moment(v).format("DD-MM-YYYY")
      : v;
  }
  return v;
};

const text = (v: any) => (v === null || v === undefined || v === "" ? "--" : v);

const fmtAmt = (v: any) =>
  v === null || v === undefined || v === "" ? "--" : formatAmountWithCommas(v);

const PaymentRequestTable: FunctionComponent<PaymentRequestTableProps> = ({
  rows,
  page,
  total,
  count,
  onPageChange,
  onShowHistory,
  onShowAttachment,
  variant,
}) => {
  const prIdHref = (d: PaymentRequestModel) => {
    if (variant === "approval") {
      return d?.type === "ADVANCE"
        ? `${urls.po_payment_request_approval}/approveAdvance/${d?.id}`
        : `${urls.po_payment_request_approval}/approveInvoice/${d?.id}`;
    }
    return d?.type === "ADVANCE"
      ? `${urls.po_advance_payment_request}/updateAdvance/${d?.id}`
      : `${urls.po_advance_payment_request}/updateInvoice/${d?.id}`;
  };

  const poHref = (d: PaymentRequestModel) =>
    variant === "approval"
      ? `${urls.purchase_order}/update/${d?.po_id}`
      : `${urls.purchase_order}/view-detail/${d?.po_id}`;

  const prIdLink = (d: PaymentRequestModel) => (
    <a
      id={`payment_request_row_${d?.id}_link`}
      href={prIdHref(d)}
      className="text-blue-500 hover:text-blue-700"
    >
      {d?.id}
    </a>
  );

  const poLink = (d: PaymentRequestModel) => (
    <a
      id={`payment_request_row_${d?.id}_po_link`}
      href={poHref(d)}
      className="text-blue-500 hover:text-blue-700"
    >
      {d?.po_no}
    </a>
  );

  const columns: Column[] = [
    { label: "PR ID #", render: (d) => prIdLink(d) },
    { label: "PO #", render: (d) => poLink(d) },
    { label: "Type", render: (d) => text(d.type) },
    { label: "PO Date", render: (d) => fmtDate(d.po_date) },
    { label: "Created Date", render: (d) => fmtDate((d as any).created_at) },
    { label: "Created By", render: (d) => text(d.created_by_name) },
    { label: "Vendor", render: (d) => text((d as any).vendor) },
    { label: "Due Date", render: (d) => fmtDate(d.due_date) },
    { label: "Payment Mode", render: (d) => text(d.payment_mode) },
    { label: "Invoice Number", render: (d) => text((d as any).invoice_no) },
    { label: "Invoice Date", render: (d) => fmtDate((d as any).invoice_date) },
    {
      label: "Invoice Amount",
      align: "right",
      render: (d) => fmtAmt((d as any).invoice_amount),
    },
    {
      label: "Customer Location",
      render: (d) => text((d as any).customer_location),
    },
    {
      label: "Category of Expense",
      render: (d) => text((d as any).category_of_expence),
    },
    {
      label: "No. of Installations",
      render: (d) => text((d as any).no_of_intallations),
    },
    { label: "PI NO", render: (d) => text((d as any).pi_no) },
    { label: "PI Date", render: (d) => fmtDate((d as any).pi_date) },
    {
      label: "PI Amount",
      align: "right",
      render: (d) => fmtAmt((d as any).pi_amount),
    },
    {
      label: "Payment Amount",
      align: "right",
      render: (d) => currencyFormat(d.payment_amount),
    },
    { label: "Project", render: (d) => text((d as any).project) },
    { label: "Note", render: (d) => text(d.notes) },
    {
      label: "PO Approval",
      render: (d) => (
        <StatusBadge status={(d as any).po_approval} kind="generic" />
      ),
    },
    { label: "SPOC", render: (d) => text(d.created_by_name) },
    { label: "UTR", render: (d) => text((d as any).utr) },
    { label: "Swift", render: (d) => text((d as any).swift) },
    {
      label: "TDS Amount",
      align: "right",
      render: (d) => fmtAmt((d as any).tds_amount),
    },
    {
      label: "TDS Percent",
      align: "right",
      render: (d) => text((d as any).tds_percent),
    },
    {
      label: "Paid Amount",
      align: "right",
      render: (d) => fmtAmt((d as any).amount_paid),
    },
    {
      label: "Remarks",
      render: (d) => text((d as any).paid_remarks ?? (d as any).failed_remarks),
    },
    {
      label: "Payment Status",
      render: (d) => <StatusBadge status={d.payment_status} kind="generic" />,
    },
    { label: "Payment Date", render: (d) => fmtDate(d.payment_date) },
    { label: "Approved At", render: (d) => fmtDate((d as any).approved_at) },
    {
      label: "Approved by",
      render: (d) => text((d as any).approved_by_name),
    },
    { label: "OnHold Reason", render: (d) => text((d as any).onhold_reason) },
    { label: "Reject Reason", render: (d) => text((d as any).reject_reason) },
    {
      label: "PR Status",
      render: (d) => (
        <StatusBadge status={(d as any).pr_status} kind="generic" />
      ),
    },
    {
      label: "Attachment",
      render: (d) => (
        <button
          id={`payment_request_row_${d?.id}_attachments_btn`}
          onClick={() => onShowAttachment(d)}
          className="text-sm font-semibold text-blue-400 hover:text-blue-500 whitespace-nowrap flex items-center gap-1"
        >
          View
          <span>({d?.attachments?.length ?? 0})</span>
        </button>
      ),
    },
  ];

  const alignClass = (a?: Align) =>
    a === "right" ? "text-right" : "text-left";

  return (
    <>
      {/* Desktop: table */}
      <div className="hidden md:block my-2 overflow-auto">
        <table id="payment_request_table" className="w-full text-xs divide-y divide-gray-200 relative">
          <thead className="text-xs bg-white z-10">
            <tr>
              <th className="px-2 py-2 text-left font-semibold whitespace-nowrap">
                Actions
              </th>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={`px-3 py-2 font-semibold whitespace-nowrap ${alignClass(
                    c.align,
                  )}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((d, i) => (
              <tr id={`payment_request_row_${d?.id}`} key={i} className="hover:bg-gray-50">
                <td className="px-2 py-2">
                  <button
                    id={`payment_request_row_${d?.id}_history_btn`}
                    title="History"
                    onClick={() => onShowHistory(d)}
                    className="text-gray-500 hover:text-myPrimaryColor"
                  >
                    <Icon path={mdiHistory} className="h-4 w-4" />
                  </button>
                </td>
                {columns.map((c, j) => (
                  <td
                    key={j}
                    className={`px-3 py-2 whitespace-nowrap ${alignClass(
                      c.align,
                    )}`}
                  >
                    {c.render(d)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <MobileRecordCardList>
        {rows.map((d, i) => (
          <MobileRecordCard
            id={`payment_request_row_mobile_${d?.id}`}
            key={i}
            title={prIdLink(d)}
            status={<StatusBadge status={d.payment_status} kind="generic" />}
            subStatus={
              <StatusBadge status={(d as any).pr_status} kind="generic" />
            }
            fields={[
              { label: "PO #", value: poLink(d) },
              { label: "Vendor", value: text((d as any).vendor) },
              { label: "Due Date", value: fmtDate(d.due_date) },
              {
                label: "Payment Amount",
                value: currencyFormat(d.payment_amount),
              },
            ]}
            actions={[
              {
                icon: mdiHistory,
                title: "History",
                onClick: () => onShowHistory(d),
              },
              {
                icon: mdiPaperclip,
                title: "Attachments",
                onClick: () => onShowAttachment(d),
              },
            ]}
          />
        ))}
      </MobileRecordCardList>

      <div id="payment_request_pagination" className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2">
        <Pagination
          itemCount={rows.length}
          page={page}
          total={total}
          count={count}
          onChange={onPageChange}
        />
      </div>
    </>
  );
};

export default PaymentRequestTable;
